import "../globals.css";
import "../style/Global.scss";
import "../styles/seatmap-editor.css";

import React, { useState } from "react";
import { useRouter } from "next/router";
import { Provider } from "react-redux";
import { store } from "../store/store";
import { SessionProvider, SessionProviderProps } from "next-auth/react";
import { ThemeConfig } from "../components/admin/ThemeProvider";
import { Toaster } from "../ui";

import { StoreThemeConfig } from "../components/StoreThemeConfig";
import { ThemeProvider } from "../components/ThemeProvider";
import { ErrorBoundary } from "../components/ErrorBoundary";
import {
  AdminErrorBoundary,
  CheckoutErrorBoundary,
  SuperAdminErrorBoundary,
  EventErrorBoundary,
  PublicErrorBoundary
} from "../components/error-boundaries";
import appWithI18n from "next-translate/appWithI18n";
import i18nConfig from "../../i18n-cached";
import axios from "axios";
import Head from "next/head";
import { OnbordaProvider } from '../shims/onborda';
import { HighlightInit } from '../components/HighlightInit';

// Initialize i18n cache on server-side
import "../lib/i18nInit";






const Global: React.FunctionComponent<{Component: any, pageProps: any}> = ({ Component, pageProps }) => {
    const router = useRouter();

    if (typeof window !== 'undefined') {
        axios.defaults.baseURL = window.location.origin;
    }

    // Choose appropriate error boundary based on route
    const getErrorBoundaryForRoute = (pathname: string) => {
        if (pathname.startsWith('/admin')) {
            return AdminErrorBoundary;
        }
        if (pathname.startsWith('/super-admin')) {
            return SuperAdminErrorBoundary;
        }
        if (pathname.startsWith('/checkout') || pathname.startsWith('/payment')) {
            return CheckoutErrorBoundary;
        }
        if (pathname.startsWith('/events') || pathname.startsWith('/seatselection')) {
            return EventErrorBoundary;
        }
        return PublicErrorBoundary;
    };

    const ErrorBoundaryComponent = getErrorBoundaryForRoute(router.pathname);

    return (
        <>
            <Head>
                <link rel="icon" type="image/png" href="/favicon.png" />
                <link rel="shortcut icon" type="image/png" href="/favicon.png" />
                <link rel="apple-touch-icon" href="/favicon.png" />
            </Head>
            {/* Initialize Highlight.io */}
            <HighlightInit />
            <ErrorBoundaryComponent>
                <Provider store={store}>
                    <ThemeProvider organisation={pageProps.organisation}>
                        <StoreThemeConfig customTheme={pageProps.theme}>
                            <Component
                                key={router.pathname}
                                {...pageProps}
                            />
                        </StoreThemeConfig>
                    </ThemeProvider>
                </Provider>
            </ErrorBoundaryComponent>
        </>
    );
}

// Main App component that handles routing logic
const App: React.FunctionComponent<{Component: any, pageProps: any}> = ({ Component, pageProps }) => {
    const router = useRouter();

    // Wrap everything with SessionProvider to ensure it's available for Highlight identification
    const content = (
        <SessionProvider session={pageProps.session}>
            <Global Component={Component} pageProps={pageProps} />
        </SessionProvider>
    );

    // Special cases that need different wrapping
    if (router.pathname === "/refund") {
        return (
            <SessionProvider session={pageProps.session}>
                <StoreThemeConfig customTheme={pageProps.theme}>
                    <Global Component={Component} pageProps={pageProps} />
                </StoreThemeConfig>
            </SessionProvider>
        );
    }

    if (router.pathname.startsWith("/admin")) {
        // Use regular imports for admin routes to prevent blank screen issues
        return (
            <OnbordaProvider>
                {content}
            </OnbordaProvider>
        );
    }

    if (router.pathname.startsWith("/super-admin")) {
        return content;
    }

    return content;
};

export default appWithI18n(App, {
    ...i18nConfig,
    skipInitialProps: true,
    loadLocaleFrom: i18nConfig.loadLocaleFrom
});
